<script setup lang="ts">
  import {
    ButtonVariant,
    disabledClass,
    shake,
    textEllipsisClass,
  } from '@tundr/theme';
  import type { TUNDR_ICONS } from '@tundr/theme-assets/tundr-icons/tundr-icons-icons';
  import { computed, ref, useAttrs, useSlots } from 'vue';
  import FontIcon from '../FontIcon/FontIcon.vue';
  import { LoaderSize } from '../Loader';
  import Loader from '../Loader/Loader.vue';
  import { Tooltip } from '../Tooltip';
  import {
    btnAlignment,
    btnIconLeft,
    btnIconRight,
    btnSize,
    btnVariants,
    btnWidth,
    buttonWithoutEllipsisClass,
  } from './Button.css';
  import {
    ButtonAlignmentVariant,
    type ButtonProps,
    ButtonSizes,
    ButtonTypes,
    ButtonWidthVariant,
  } from './Button.types';

  const props = withDefaults(defineProps<ButtonProps>(), {
    role: 'button',
    type: ButtonTypes.BUTTON,
    width: ButtonWidthVariant.AUTO,
    size: ButtonSizes.DEFAULT,
    textEllipsis: true,
    loading: false,
    disabled: false,
    variant: ButtonVariant.primary,
    textAlign: ButtonAlignmentVariant.CENTER,
  });
  const emits = defineEmits<{
    click: [event: Event];
    middleClick: [event: MouseEvent];
  }>();

  const isSubmitting = ref<boolean>(false);

  const slots = useSlots();
  const attrs = useAttrs();

  defineOptions({
    inheritAttrs: false,
  });

  // we get the button text from default slot to use it in aria-label
  const buttonText = computed(() => {
    return slots?.default && (slots?.default()[0].children as string);
  });

  const baseClasses = computed(() => [
    attrs.class,
    btnVariants[props.variant],
    btnWidth[props.width],
    btnAlignment[props.textAlign],
    btnSize[props.size],
  ]);

  const isInteractionBlocked = computed(() => props.disabled || props.loading);

  const conditionalClasses = computed(() => {
    return {
      [textEllipsisClass]: props.textEllipsis,
      [buttonWithoutEllipsisClass]: props.textEllipsis === false,
      [disabledClass]: isInteractionBlocked.value,
      [shake]: isSubmitting.value,
    };
  });

  const buttonTextClass = computed(() => ({
    [textEllipsisClass]: props.textEllipsis,
  }));

  const handleClick = (e: Event) => {
    if (
      props.type === ButtonTypes.SUBMIT &&
      (props.disabled || props.loading)
    ) {
      isSubmitting.value = true;
      setTimeout(() => {
        isSubmitting.value = false;
      }, 500);
      e.preventDefault();
      return;
    }
    if (!props.disabled && !props.loading) {
      emits('click', e);
    }
  };

  const handleAuxClick = (e: MouseEvent) => {
    if (!props.disabled && !props.loading) {
      emits('middleClick', e);
    }
  };
</script>

<template>
  <template v-if="tooltipText">
    <Tooltip
      v-if="tooltipText"
      :label="tooltipText"
      :description="tooltipDescription"
      :class="tooltipClass"
    >
      <button
        :id="id || testId"
        :data-test-id="testId"
        :title="title"
        :role="role"
        :type="type"
        :class="[baseClasses, conditionalClasses]"
        :aria-label="buttonText"
        :aria-disabled="isInteractionBlocked || undefined"
        @click="handleClick"
        @middle.stop.prevent="handleAuxClick"
        :is-disabled="disabled"
      >
        <slot name="iconLeft">
          <div
            :data-test-id="testId"
            v-if="iconName && !loading"
            :class="btnIconLeft"
          >
            <FontIcon :icon-name="iconName as TUNDR_ICONS" />
          </div>
        </slot>
        <template v-if="loading">
          <Loader :size="LoaderSize.small" />
        </template>
        <template v-else>
          <span :data-test-id="testId" :class="buttonTextClass"
            ><slot>{{ label }}</slot></span
          >
        </template>
        <div v-if="slots.iconRight" :class="btnIconRight">
          <slot name="iconRight" />
        </div>
      </button>
    </Tooltip>
  </template>
  <template v-else>
    <button
      :id="id || testId"
      :data-test-id="testId"
      :title="title"
      :role="role"
      :type="type"
      :class="[baseClasses, conditionalClasses]"
      :aria-label="buttonText"
      :aria-disabled="isInteractionBlocked || undefined"
      @click="handleClick"
      @auxclick="handleAuxClick"
      :is-disabled="disabled"
    >
      <slot name="iconLeft">
        <div v-if="iconName && !loading" :class="btnIconLeft">
          <FontIcon :data-test-id="testId" :icon-name="iconName" />
        </div>
      </slot>
      <div v-if="loading"><Loader :size="LoaderSize.small" /></div>
      <span :data-test-id="testId" :class="buttonTextClass" v-else
        ><slot>{{ label }}</slot></span
      >
      <div v-if="slots.iconRight" :class="btnIconRight">
        <slot name="iconRight" />
      </div>
    </button>
  </template>
</template>
